import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCartStore } from "@/stores/cartStore";
import { useCnhCheckStore } from "@/stores/cnhCheckStore";
import { isOnlineEmission } from "@/lib/catalog";
import { trackEvent } from "@/lib/analytics";
import type { Product } from "@/types/api";

/**
 * "Buy now" gesture: add one or more products to the cart, remember the
 * page the user came from (so "Continuar comprando" in checkout can return
 * them to the exact same listing), and navigate to /checkout.
 *
 * Before proceeding, if ANY item is an online-emission certificate, the
 * global CNH verification dialog is triggered. The user must either
 * confirm they have a CNH post-2017 or switch the items to the presential
 * equivalents.
 */
export const CHECKOUT_ORIGIN_KEY = "checkout:origin";

export function useBuyNow() {
  const navigate = useNavigate();
  const location = useLocation();
  const addProduct = useCartStore((state) => state.addProduct);
  const requestCnhCheck = useCnhCheckStore((state) => state.request);

  return useCallback(
    (products: Product | Product[], quantity = 1) => {
      const list = Array.isArray(products) ? products : [products];

      const proceed = (finalList: Product[]) => {
        for (const product of finalList) {
          // silent=true pula o CartAddedModal — o buy-now flow já
          // redireciona pro /checkout e o modal "Pagar agora" viraria
          // botão redundante sobre a própria tela de pagamento.
          addProduct(product, quantity, { silent: true });
          trackEvent("add_to_cart", {
            productId: product.id,
            sku: product.sku,
            name: product.name,
            priceCents: product.priceCents,
            quantity,
          });
        }
        trackEvent("checkout_start", {
          itemCount: finalList.length,
          totalCents: finalList.reduce((s, p) => s + p.priceCents * quantity, 0),
        });
        try {
          sessionStorage.setItem(
            CHECKOUT_ORIGIN_KEY,
            location.pathname + location.search
          );
        } catch {
          // sessionStorage can be unavailable in private mode — fall back silently.
        }
        navigate("/checkout");
      };

      const hasOnlineCertificate = list.some(isOnlineEmission);
      if (hasOnlineCertificate) {
        requestCnhCheck(list, (finalList, resolution) => {
          if (resolution !== "canceled") proceed(finalList);
        });
        return;
      }

      proceed(list);
    },
    [addProduct, location, navigate, requestCnhCheck]
  );
}
